import type { FC, ReactNode } from 'react'

import { Mail, MapPin, Phone } from 'lucide-react'
import { Reveal } from '@/components/generals/reveal'
import { Container } from '@/components/generals/container'
import { RevealText } from '@/components/generals/reveal-text'
import { SilkDrift } from '@/components/dots/silk-drift'
import { SectionLabel } from '@/components/landing/section-label'
import { ContactForm } from '@/components/landing/contact-form'
import { siteConfig } from '@/constants/site'

interface ContactSectionProps {
    index?: string
    label?: string
    lines?: Array<string>
}

export const ContactSection: FC<ContactSectionProps> = ({
    index = '05',
    label = "Contact",
    lines = ["Let’s build", "what’s next."]
}): ReactNode => {
    const { contact } = siteConfig

    return (
        <section id='contact' className='relative isolate border-t border-line bg-white'>
            <SilkDrift />

            <Container className='grid gap-16 py-24 md:py-36 lg:grid-cols-12'>
                <div className='lg:col-span-5'>
                    <SectionLabel index={index}>{label}</SectionLabel>

                    <RevealText
                        lines={lines}
                        className='mt-8 text-[clamp(3rem,6.5vw,6rem)] font-medium leading-[0.92] tracking-[-0.05em] text-ink'
                    />

                    <Reveal>
                        <p className='mt-8 max-w-md text-lg leading-relaxed text-ink/70'>
                            Tell us about your product, your timeline and what success looks like. A partner replies within one business day.
                        </p>

                        <ul className='mt-12 flex flex-col border-t border-line'>
                            <li className='border-b border-line'>
                                <a
                                    href={`mailto:${contact.email}`}
                                    className='group flex items-center gap-4 py-5 text-ink transition-colors hover:text-azure-600'
                                >
                                    <Mail aria-hidden='true' className='size-5 text-azure-500' />
                                    <span className='text-lg tracking-[-0.01em]'>{contact.email}</span>
                                </a>
                            </li>

                            <li className='border-b border-line'>
                                <a
                                    href={`tel:${contact.phoneHref}`}
                                    className='group flex items-center gap-4 py-5 text-ink transition-colors hover:text-azure-600'
                                >
                                    <Phone aria-hidden='true' className='size-5 text-azure-500' />
                                    <span className='text-lg tracking-[-0.01em]'>{contact.phone}</span>
                                </a>
                            </li>

                            <li className='flex items-center gap-4 border-b border-line py-5 text-ink'>
                                <MapPin aria-hidden='true' className='size-5 shrink-0 text-azure-500' />
                                <span className='text-lg tracking-[-0.01em]'>
                                    {contact.street}, {contact.city}
                                </span>
                            </li>
                        </ul>
                    </Reveal>
                </div>

                <div className='lg:col-span-6 lg:col-start-7'>
                    <Reveal delay={120}>
                        <ContactForm />
                    </Reveal>
                </div>
            </Container>
        </section>
    )
}
